/**
 * ErrorBoundary — last line of defense for render crashes. Shows a friendly
 * centered card (never a white screen) and reports the error via the
 * fire-and-forget telemetry endpoint. Wire it around the app in App.tsx.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { TriangleAlert } from './icons';
import { Button } from './ui';

/** Functional fallback so we can use the i18n hook (the boundary itself must be a class). */
function ErrorFallback() {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[60dvh] items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-4 rounded-2xl border border-border bg-surface p-8 text-center">
        <div className="flex size-14 items-center justify-center rounded-2xl bg-negative/15 text-negative" aria-hidden>
          <TriangleAlert className="size-6" />
        </div>
        <p className="text-sm text-muted">{t('common.error')}</p>
        <Button variant="surface" onClick={() => location.reload()}>
          {t('common.retry')}
        </Button>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Fire-and-forget — api.telemetry swallows its own failures.
    void api.telemetry([
      {
        level: 'error',
        message: error.message,
        stack: error.stack ?? info.componentStack ?? undefined,
        url: location.href,
      },
    ]);
  }

  render(): ReactNode {
    if (this.state.error) return <ErrorFallback />;
    return this.props.children;
  }
}

export default ErrorBoundary;
